import {
  ActionType,
  BarOpeningSide,
  OptionInstance,
  RailingOpeningPosition,
  StyleModel,
} from '@swd_admin/cdc-viewer';
import {
  isUrbanPavilionProduct,
  useAppState,
  UserStateDimensions,
  UserStateMaterials,
} from 'state/appstate/appstate';
import { useViewerState } from 'state/viewer-state/viewer-state';
import { updateBom } from './api';
import { BOMOptionInstance, Option } from '../api';
import { showRenderResult, useUiState } from '../state/ui-state/ui-state';
import { PaintColor } from '../components/materialssidebar/tabpane';
import { nanoid } from 'nanoid';

const doorHandlers = ['DH1A', 'DH9B', 'DH3A', 'RP2BP', 'RU6BI'];

export type UserDefinableParams<T> = Array<{
  uStateKey: keyof T;
  viewerParameter: keyof StyleModel.RenderParameters;
}>;

export const getUserDefinableMaterialsParams = ():
  | Partial<StyleModel.RenderParameters>
  | undefined => {
  const userDefinableParams: UserDefinableParams<UserStateMaterials> = [
    { uStateKey: 'siding', viewerParameter: 'style_wall_materialId' },
    { uStateKey: 'trim', viewerParameter: 'style_trimming_materialId' },
    { uStateKey: 'roofing', viewerParameter: 'style_roof_materialId' },
  ];
  const { uState, dicts } = useAppState.getState();
  if (!uState) {
    return {};
  }

  return userDefinableParams.reduce((result: any, item) => {
    const materialId = uState!.materials[item.uStateKey]!;
    if (materialId) {
      const material = dicts.materialsDbIdToShapeDiverId![materialId];
      if (!material) {
        console.warn(
          `material with db id ${materialId} doesn't have shapeDiver id (probably because it wasn't received from the backend)`
        );
      } else {
        result[item.viewerParameter] = material;
      }
    }
    return result;
  }, {});
};

export const getDefaultConfigurationGhModelJson = () => {
  const { styleProduct } = useAppState.getState();
  return (
    JSON.parse(
      styleProduct.style?.defaultConfiguration?.style.ghModelJson! || '{}'
    ) || {}
  );
};
export const getDefaultConfigurationRenderParameters = () => {
  const { renderParameters = {} } = getDefaultConfigurationGhModelJson();
  return renderParameters;
};

export const getUserDefinableDimensionsParams = ():
  | Partial<StyleModel.RenderParameters>
  | undefined => {
  const { uState } = useAppState.getState();
  if (!uState?.dimensions) {
    return;
  }

  const { style_ceilingHeight } = getDefaultConfigurationRenderParameters();
  return {
    style_depth: uState.dimensions.depth,
    style_width: uState.dimensions.width,
    style_heightToCeiling:
      uState.dimensions.wallHeight - 
      (isNaN(Number(style_ceilingHeight)) ? 0 : Number(style_ceilingHeight)),
  };
};

enum StyleRoofType {
  Custom = 0,
  Flat = 1,
  Skillion1 = 2,
  Skillion2 = 3,
  Gable = 4,
  Hip = 5,
  PyramidHip = 6,
  Dutch = 7,
  Gambrel = 8,
  Mansard = 9,
}

const getUserDefinableOverhangParams =
  (): Partial<StyleModel.RenderParameters> => {
    const { uState } = useAppState.getState();
    if (!uState) {
      return {};
    }

    const { overhang } = uState;
    const userDefinableOverhangParams = {
      style_roof_eaveOverhang_0: overhang?.toString() as any,
      style_roof_eaveOverhang_1: overhang?.toString() as any,
      style_roof_eaveOverhang_2: overhang?.toString() as any,
      style_roof_eaveOverhang_3: overhang?.toString() as any,
    };

    const style = getDefaultConfigurationGhModelJson();
    const { renderParameters, id } = style;
    const { style_roof_type } = renderParameters || {};

    if (id?.toString() === '21') {
      return {
        style_wall_offset_1: overhang?.toString() as any,
      };
    } else if (id.toString() === '64' || id.toString() === '75') {
      const {
        style_roof_eaveOverhang_0,
        style_roof_eaveOverhang_2,
        style_roof_eaveOverhang_3,
        ...parameters
      } = userDefinableOverhangParams;
      return parameters;
    } else if (
      (style_roof_type &&
        [
          StyleRoofType.Flat,
          StyleRoofType.Skillion1,
          StyleRoofType.Skillion2,
        ].includes(style_roof_type)) ||
      id.toString() === '20' ||
      id.toString() === '77'
    ) {
      return {};
    } else if (style_roof_type === StyleRoofType.Gable) {
      const {
        style_roof_eaveOverhang_0,
        style_roof_eaveOverhang_2,
        ...parameters
      } = userDefinableOverhangParams;
      return parameters;
    } else {
      return userDefinableOverhangParams;
    }
  };

export const getUserDefinableInitParams =
  (): Partial<StyleModel.RenderParameters> => {
    return {
      ...(getUserDefinableDimensionsParams() || {}),
      ...(getUserDefinableMaterialsParams() || {}),
      ...(getUserDefinableOverhangParams() || {}),
    };
  };

export const updateRenderParameters = (
  parameters: Partial<StyleModel.RenderParameters> | undefined
) => {
  if (parameters) {
    const { viewer } = useViewerState.getState();
    viewer?.dispatch({
      type: ActionType.UpdateRenderParameters,
      parameters,
    });
    showRenderResult();
  }
};

export const addOption = async (optionTypeId: string, definitionId: string) => {
  const appState = useAppState.getState();
  const { style } = appState?.styleProduct;
  const { viewer } = useViewerState.getState();
  let localPosition = { u: 0.5, v: 0 };

  if (optionTypeId === 'windows') {
    localPosition = { u: 0.5, v: 0.5 };
  }

  const option = style!.options.find((o) => o.orderCode === definitionId);
  const optionType =
    option &&
    appState.styleProduct.style?.optionTypes.find(
      ({ id }) => option.optionTypeId === id
    );
  const ghModelJson = JSON.parse(option?.ghModelJson || JSON.stringify(''));

  const isUrbanPavilion = isUrbanPavilionProduct();
  //TODO: This is a temporary fix to add the option to the BOM. We need to find a better way to do this and add more options to the BO.
  if(!!ghModelJson && option && optionType && isUrbanPavilion && ['material_upgrades', 'post_and_beam'].includes(optionType?.id)) {
    // We need to add only one option that is ceilling and soffit boards.
    const optionsArrayWithoutCeillingAndSoffitBoards = style!.options.filter(opt => !opt.orderCode.includes('CSB'))
    const idsWithoutCeillingAndSoffitBoards = optionsArrayWithoutCeillingAndSoffitBoards.map(opt => opt.id)

    // Material Finish options - only one can be selected at a time
    const materialFinishCodes = ['EXBR', 'CLEAR-WRAP', 'STK-WRAP'];
    const isMaterialFinishOption = materialFinishCodes.includes(option.orderCode);

    // Build a list of ALL definition IDs related to Material Finish options (including components)
    const materialFinishAllDefinitionIds: string[] = [];
    style!.options.filter(opt => materialFinishCodes.includes(opt.orderCode)).forEach(opt => {
      // Add the main option id
      materialFinishAllDefinitionIds.push(opt.id);
      // Also add any component ids from ghModelJson
      const optGhModelJson = JSON.parse(opt.ghModelJson || '{}');
      if (optGhModelJson.components) {
        optGhModelJson.components.forEach((comp: any) => {
          materialFinishAllDefinitionIds.push(comp.id);
        });
      }
    });

    let state = optionType?.id === 'post_and_beam' ? appState?.uState?.bomOnlyOptions : appState?.uState?.bomOnlyOptions.filter(opt => idsWithoutCeillingAndSoffitBoards.includes(opt.definitionId));

    // If adding a Material Finish option on Urban Pavilion, remove any existing Material Finish options from BOM
    if (isUrbanPavilion && isMaterialFinishOption && state) {
      state = state.filter(opt => !materialFinishAllDefinitionIds.includes(opt.definitionId));
    }
    const newOption: BOMOptionInstance[] = [];
    if(ghModelJson.components) {
      ghModelJson.components.map((component: any) => newOption.push({
        definitionId: component.id,
        parentId: optionType.snapClass,
        id: nanoid(),
      }))
      newOption.push({
        definitionId: option.id,
        parentId: optionType.snapClass,
        id: nanoid(),
      })
    } else {
      newOption.push({
        definitionId: option.id,
        parentId: optionType.snapClass,
        id: nanoid(),
      })
    }
    appState.setBomOnlyOptions([
      ...(state || []),
      ...newOption,
    ]);
    console.log('ghModelJson.updateParameters', ghModelJson.updateParameters)
    updateRenderParameters(ghModelJson.updateParameters);
    
    return;
  }

  if (!!ghModelJson) {
    const { extraInfo } = ghModelJson;
    viewer?.dispatch({
      type: ActionType.AddOption,
      optionId: definitionId,
      localPosition: extraInfo?.defaultPosition || localPosition,
    });

    showRenderResult();
  } else if (option && optionType) {
    appState.setBomOnlyOptions([
      ...(appState?.uState?.bomOnlyOptions || []),
      {
        definitionId: option.id,
        parentId: optionType.snapClass,
        id: nanoid(),
      },
    ]);
  }
};


export const removeOption = (instanceId: string) => {
  const appState = useAppState.getState();
  const bomOnlyOption = appState?.uState?.bomOnlyOptions.find(
    ({ id }) => id === instanceId
  );
  const idsForDelete: string[] = [];
  const rawOption = appState?.uState?.options.find(opt => opt.id === bomOnlyOption?.definitionId);
  const ghModelJson = JSON.parse(rawOption?.ghModelJson || JSON.stringify(''));
  if(rawOption?.ghModelJson &&  ghModelJson.components && bomOnlyOption) {
     ghModelJson.components.map((component: any) => idsForDelete.push(component.id))
     idsForDelete.push(rawOption.id)
  } else if (bomOnlyOption) {
     idsForDelete.push(bomOnlyOption.definitionId);
  }

  const optionsArrayWithCeillingAndSoffitBoards = appState?.uState?.options.filter(opt => opt.orderCode.includes('CSB'))
  const ceillingAndSoffitBoardsIDs = optionsArrayWithCeillingAndSoffitBoards?.map(opt => opt.id)
  const isUrbanPavilion = isUrbanPavilionProduct();
  if(bomOnlyOption && isUrbanPavilion && ceillingAndSoffitBoardsIDs?.includes(bomOnlyOption.definitionId)) {
    const { viewer } = useViewerState.getState();
    viewer?.dispatch({
      type: ActionType.DeleteOption,
      instanceId,
    });

    showRenderResult();
  }

  if (bomOnlyOption) {
    const filtered = appState.uState!.bomOnlyOptions.filter(o => !idsForDelete.includes(o.definitionId));

    // Urban Pavilion: re-add default option when a non-default is removed
    if (isUrbanPavilion && rawOption) {
      const defaultRestorations: Record<string, string> = {
        'CLEAR-WRAP': 'EXBR',
        'STK-WRAP': 'EXBR',
        'CCCSB': 'STKCCSB',
      };
      const defaultCode = defaultRestorations[rawOption.orderCode];
      if (defaultCode) {
        const { style } = appState.styleProduct;
        const defaultOption = style?.options.find(o => o.orderCode === defaultCode);
        const defaultOptionType = defaultOption && style?.optionTypes.find(({ id }) => defaultOption.optionTypeId === id);
        if (defaultOption && defaultOptionType && !filtered.some(o => o.definitionId === defaultOption.id)) {
          filtered.push({
            definitionId: defaultOption.id,
            parentId: defaultOptionType.snapClass,
            id: nanoid(),
          });
          const defaultGhModel = JSON.parse(defaultOption.ghModelJson || '{}');
          if (defaultGhModel.updateParameters) {
            updateRenderParameters(defaultGhModel.updateParameters);
          }
        }
      }
    }

    appState.setBomOnlyOptions(filtered);
  } else {
    const { viewer } = useViewerState.getState();
    viewer?.dispatch({
      type: ActionType.DeleteOption,
      instanceId,
    });

    showRenderResult();
  }
};

export const changeBarOpeningSide = (openingSide: BarOpeningSide) => {
  const { viewer } = useViewerState.getState();
  viewer?.dispatch({
    type: ActionType.ChangeBarOpeningSide,
    openingSide,
  });

  showRenderResult();
};

export const setOpenAreaLength = (length: number) => {
  const { viewer } = useViewerState.getState();
  viewer?.dispatch({
    type: ActionType.SetOpenAreaLength,
    length,
  });

  showRenderResult();
};

export const changeOpenAreaOrientation = () => {
  const { viewer } = useViewerState.getState();
  viewer?.dispatch({
    type: ActionType.ChangeOpenAreaOrientation,
  });

  showRenderResult();
};

export const toggleBarOpeningOption = () => {
  const { viewer } = useViewerState.getState();
  viewer?.dispatch({
    type: ActionType.ToggleBarOpeningOption,
  });

  showRenderResult();
};

export const changRailingOpeningPosition = (
  position: RailingOpeningPosition
) => {
  const { viewer } = useViewerState.getState();
  viewer?.dispatch({
    type: ActionType.ChangRailingOpeningPosition,
    position,
  });

  showRenderResult();
};

export const changeAnnexWallOpening = () => {
  const { viewer, viewerState } = useViewerState.getState();

  ['personalAccessDoor', 'garageDoor']?.forEach((instanceId) => {
    const instance = viewerState?.style.instances?.find(
      ({ id }) => id === instanceId
    );
    if (instance && instance.localPosition?.u) {
      viewer?.dispatch({
        type: ActionType.MoveOption,
        id: instanceId,
        localPosition: {
          ...instance.localPosition,
          u: 1 - +instance.localPosition.u,
        },
      });
    }
  });

  showRenderResult();
};

export const addFeature = (feature: Option, optionInstance: OptionInstance) => {
  const appState = useAppState.getState();
  const { style } = appState?.styleProduct;
  const { viewer } = useViewerState.getState();

  const optionDefinition = JSON.parse(
    style!.options.find((o) => o.orderCode === optionInstance.optionId)
      ?.ghModelJson || JSON.stringify({ externalFeatures: [] })
  );
  const { externalFeatures } = optionDefinition;
  const externalFeatureDefinition =
    externalFeatures?.find((f: OptionInstance) => f.id === feature.orderCode) ||
    (doorHandlers.find((id) => id === feature.orderCode) &&
      externalFeatures?.find((f: OptionInstance) =>
        doorHandlers.some((id) => id === f.id)
      ));

  viewer?.dispatch({
    type: ActionType.AddOption,
    optionId: feature.orderCode,
    parentId: optionInstance.id,
    conflictOptionIds: feature.conflictOptionIds,
    localPosition: externalFeatureDefinition?.localPosition,
  });

  showRenderResult();
};

export const removeFeature = (instanceId: string) => {
  const { viewer } = useViewerState.getState();
  viewer?.dispatch({
    type: ActionType.DeleteOption,
    instanceId,
  });

  showRenderResult();
};

export const updateOptionParameters = (
  instanceId: string,
  key: string,
  value: string | boolean
) => {
  const { viewer } = useViewerState.getState();

  viewer?.dispatch({
    type: ActionType.ChangeOptionTransformationState,
    instanceId,
    state: { [key]: value },
  });

  showRenderResult();
};

export const paintHouse = (selectedColor: PaintColor) => {
  const { viewer } = useViewerState.getState();

  viewer?.dispatch({
    type: ActionType.Paint,
    trim: selectedColor.trim,
    wall: selectedColor.siding,
  });
};

export const applyDimensionChanges = () => {
  const { viewer } = useViewerState.getState();
  const { removableInstanceIds, setRemovableInstanceIds } =
    useUiState.getState();
  const { uState } = useAppState.getState();

  if (removableInstanceIds) {
    viewer?.dispatch({
      type: ActionType.ForceResize,
      removableInstanceIds,
      dimensions: {
        ...getUserDefinableDimensionsParams(),
        ...getUserDefinableOverhangParams(),
      } as StyleModel.DimensionParameters,
      openAreaLength: +(uState?.openAreaSize || 0),
    });
    setRemovableInstanceIds(undefined);
  }
};

export const setConcretePadOption = (value: boolean) => {
  const { viewer } = useViewerState.getState();
  viewer?.dispatch({
    type: ActionType.ChangeConcretePad,
    concretePad: value,
  });

  showRenderResult();
};

export const SURFSIDE_ID = '60';
export const BARSIDE_ID = '72';
export const SANARA_ID = '79';

useUiState.subscribe(
  (selectedColor: PaintColor) => paintHouse(selectedColor),
  (state) => state.selectedColor
);

useAppState.subscribe(
  (materials: UserStateMaterials | undefined) =>
    {
      console.log('materials', getUserDefinableMaterialsParams())
      materials !== undefined &&
    updateRenderParameters(getUserDefinableMaterialsParams())
  },
  (state) => state.uState?.materials
);

useAppState.subscribe(
  (dimensions: UserStateDimensions | undefined) =>
    dimensions !== undefined &&
    updateRenderParameters(getUserDefinableDimensionsParams()),
  (state) => state.uState?.dimensions
);

useAppState.subscribe(
  (overhang: number | undefined) =>
    overhang !== undefined &&
    updateRenderParameters(getUserDefinableOverhangParams()),
  (state) => state.uState?.overhang
);

// Subscribe to update BOM on state change
useAppState.subscribe(
  () => updateBom(),
  (state) => state
);
useViewerState.subscribe(
  () => updateBom(),
  (state) => state.viewerState
);
